"use client";
/**
 * The interactive pet scene on the landing — three blocks, one per verb (paint / build / live+talk),
 * each the REAL scene opened in that mode. Three WebGL scenes at once would be heavy, so a block
 * mounts its scene only while it is near the viewport (IntersectionObserver) and drops it when
 * scrolled far away; all three share the same localStorage demo backend, so it is one pet.
 */
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";

const Scene = dynamic(() => import("@/contexts/habits/creature/landing/LandingPetScene").then((m) => m.LandingPetScene), {
  ssr: false,
  loading: () => <div className="absolute inset-0 animate-pulse bg-muted" aria-hidden />,
});

export type LandingDemoMode = "closeup" | "build" | "live";

function LazyScene({ mode, names }: { mode: LandingDemoMode; names: string[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const io = new IntersectionObserver(([e]) => setNear(e.isIntersecting), { rootMargin: "200px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className="relative h-128 w-full overflow-hidden rounded-3xl border border-border bg-[#1a2030] sm:h-120" data-testid={`landing-pet-scene-${mode}`}>
      {near ? <Scene mode={mode} names={names} /> : <div className="absolute inset-0 bg-muted" aria-hidden />}
    </div>
  );
}

export function LandingPetDemoBlock({ mode, emoji, title, text, petNames }: { mode: LandingDemoMode; emoji: string; title: string; text: string; petNames: string[] }) {
  return (
    <div className="space-y-3" data-testid={`landing-pets-${mode}`}>
      <div className="flex items-start gap-3">
        <div aria-hidden className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted text-2xl">{emoji}</div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{text}</p>
        </div>
      </div>
      <LazyScene mode={mode} names={petNames} />
    </div>
  );
}
